<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, onMounted, ref, watch } from "vue";
import BusinessImpactPanel from "../components/dashboard/BusinessImpactPanel.vue";
import DashboardFooterBar from "../components/dashboard/DashboardFooterBar.vue";
import DashboardHeaderBar from "../components/dashboard/DashboardHeaderBar.vue";
import DashboardStatCard from "../components/dashboard/DashboardStatCard.vue";
import DashboardStateView from "../components/dashboard/DashboardStateView.vue";
import DistributionCenterActivityCard from "../components/dashboard/DistributionCenterActivityCard.vue";
import InventoryHealthPanel from "../components/dashboard/InventoryHealthPanel.vue";
import ReasonBreakdownPanel from "../components/dashboard/ReasonBreakdownPanel.vue";
import RedistributionActivityCard from "../components/dashboard/RedistributionActivityCard.vue";
import TopInventoryWinsCard from "../components/dashboard/TopInventoryWinsCard.vue";
import {
  dashboardRunOptions,
  dashboardScenarios,
} from "../features/dashboard/dashboard.mock.ts";
import type {
  DashboardRunOption,
  DashboardScenario,
  DashboardSectionKey,
} from "../features/dashboard/dashboard.types.ts";
import { dashboardSectionLabels } from "../features/dashboard/dashboardNav.ts";
import { buildDashboardPageDataFromRun } from "../features/dashboard/dashboardTraceMapper.ts";
import {
  fetchRedistributionResult,
  fetchTraceLog,
} from "../services/apiClient.ts";
import {
  applyCompletedRedistributionResult,
  hydrateTraceLogFromResult,
} from "../services/applyRedistributionResult.ts";
import { useRedistributionQueueStore } from "../stores/redistributionQueueStore.ts";
import { useRedistributionStore } from "../stores/redistributionStore.ts";

const QUEUE_RUN_PREFIX = "queue-";

function isQueueRunId(id: string): boolean {
  return id.startsWith(QUEUE_RUN_PREFIX);
}

function queueRequestId(id: string): string {
  return id.slice(QUEUE_RUN_PREFIX.length);
}

function formatRunTimestamp(value: string | number): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const redistributionStore = useRedistributionStore();
const { traceLog, redistributionResult, originalPOs, activeRequestId } =
  storeToRefs(redistributionStore);
const hasLatestResults = computed(
  () => redistributionResult.value !== undefined
);

const queueStore = useRedistributionQueueStore();
const { items: queueItems } = storeToRefs(queueStore);

const completedRunOptions = computed<DashboardRunOption[]>(() =>
  queueItems.value
    .filter((item) => item.status === "completed")
    .map((item) => ({
      id: `${QUEUE_RUN_PREFIX}${item.requestId}`,
      label: `Redistribution Run: ${formatRunTimestamp(item.completedAt ?? item.submittedAt)}`,
    }))
);

const runOptions = computed<DashboardRunOption[]>(() => [
  ...completedRunOptions.value,
  ...dashboardRunOptions,
]);

const selectedRunId = ref(
  activeRequestId.value
    ? `${QUEUE_RUN_PREFIX}${activeRequestId.value}`
    : "completed-run"
);
const selectedSectionKeys = ref<DashboardSectionKey[]>([
  "distributionCenterActivity",
  "summaryStats",
  "insightPanels",
  "tables",
  "footerSummary",
]);

const dynamicRunLoading = ref(false);
const dynamicRunError = ref<string | undefined>(undefined);

async function ensureQueueRunLoaded(runId: string): Promise<void> {
  const requestId = queueRequestId(runId);
  if (activeRequestId.value === requestId) return;

  dynamicRunError.value = undefined;
  dynamicRunLoading.value = true;
  try {
    const result = await fetchRedistributionResult(requestId);
    applyCompletedRedistributionResult(result, requestId);
    await hydrateTraceLogFromResult(result);
  } catch (error) {
    dynamicRunError.value =
      error instanceof Error ? error.message : "Failed to load this run.";
  } finally {
    dynamicRunLoading.value = false;
  }
}

watch(
  selectedRunId,
  (id) => {
    if (isQueueRunId(id)) {
      void ensureQueueRunLoaded(id);
    }
  },
  { immediate: true }
);

const scenario = computed<DashboardScenario | undefined>(() => {
  if (isQueueRunId(selectedRunId.value)) {
    if (dynamicRunLoading.value) {
      return {
        status: "loading",
        stateTitle: "Loading redistribution run",
        stateMessage: "Fetching the results for this completed run.",
      };
    }
    if (dynamicRunError.value) {
      return {
        status: "error",
        stateTitle: "Unable to load this run",
        stateMessage: dynamicRunError.value,
      };
    }
    return dashboardScenarios["completed-run"];
  }
  return dashboardScenarios[selectedRunId.value];
});
const pageData = computed(() => {
  const baseData = scenario.value?.pageData;
  const isLiveRun =
    selectedRunId.value === "completed-run" ||
    isQueueRunId(selectedRunId.value);
  if (!baseData || !isLiveRun) {
    return baseData;
  }

  const mapperOptions = {
    ...(traceLog.value === undefined ? {} : { traceLog: traceLog.value }),
    ...(redistributionResult.value === undefined
      ? {}
      : { redistributionResult: redistributionResult.value }),
    ...(originalPOs.value.length > 0 ? { originalPOs: originalPOs.value } : {}),
  };

  return buildDashboardPageDataFromRun(baseData, {
    ...mapperOptions,
  });
});

onMounted(() => {
  const blobId = redistributionResult.value?.traceLogBlobId;
  if (!traceLog.value && blobId) {
    void fetchTraceLog(blobId)
      .then((content) => {
        redistributionStore.setTraceLog(content);
      })
      .catch(() => {
        // Keep dashboard usable even when trace retrieval fails.
      });
  }
});

const filterSummary = computed(() => {
  if (selectedSectionKeys.value.length === 0) {
    return "All landing page sections are hidden. Re-enable one or more sections to restore the control tower.";
  }
  return `Visible: ${selectedSectionKeys.value
    .map((sectionKey) => dashboardSectionLabels[sectionKey])
    .join(", ")}`;
});

function hasSection(sectionKey: DashboardSectionKey): boolean {
  return selectedSectionKeys.value.includes(sectionKey);
}

function toggleSection(sectionKey: DashboardSectionKey): void {
  selectedSectionKeys.value = selectedSectionKeys.value.includes(sectionKey)
    ? selectedSectionKeys.value.filter((value) => value !== sectionKey)
    : [...selectedSectionKeys.value, sectionKey];
}
</script>

<template>
  <div class="dashboard-page page-main-container gap-5">
    <DashboardHeaderBar
      :last-run-label="pageData?.lastRunLabel ?? 'Awaiting data'"
      :run-options="runOptions"
      :selected-run-id="selectedRunId"
      :selected-section-keys="selectedSectionKeys"
      :section-labels="dashboardSectionLabels"
      :filter-summary="pageData?.filterSummary ?? filterSummary"
      @update:selected-run-id="selectedRunId = $event"
      @toggle-section="toggleSection"
    />

    <div v-if="hasLatestResults" class="flex items-center justify-end">
      <RouterLink
        :to="{ name: 'results' }"
        class="dashboard-control dashboard-text-link inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold no-underline"
      >
        ← Back to Latest Results
      </RouterLink>
    </div>

    <!-- Sits outside the scenario branches below: these counts describe live
         open POs, not the selected run, so switching runs (or a run erroring)
         must not take them off the page. -->
    <DistributionCenterActivityCard
      v-if="hasSection('distributionCenterActivity')"
    />

    <DashboardStateView
      v-if="scenario?.status === 'loading'"
      tone="loading"
      :title="scenario.stateTitle ?? 'Loading dashboard data'"
      :message="
        scenario.stateMessage ?? 'Please wait while we refresh the latest run.'
      "
    />
    <DashboardStateView
      v-else-if="scenario?.status === 'empty'"
      tone="empty"
      :title="scenario.stateTitle ?? 'No dashboard data available'"
      :message="scenario.stateMessage ?? 'Choose another run to continue.'"
    />
    <DashboardStateView
      v-else-if="scenario?.status === 'error'"
      tone="error"
      :title="scenario.stateTitle ?? 'Dashboard unavailable'"
      :message="scenario.stateMessage ?? 'Try again later or switch runs.'"
    />
    <DashboardStateView
      v-else-if="selectedSectionKeys.length === 0"
      tone="empty"
      title="All dashboard sections are hidden"
      message="Use the Filters control in the header to restore one or more landing page sections."
    />
    <template v-else-if="pageData">
      <section
        v-if="hasSection('summaryStats')"
        class="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6"
      >
        <DashboardStatCard
          v-for="stat in pageData.summaryStats"
          :key="stat.id"
          :stat="stat"
        />
      </section>

      <section
        v-if="hasSection('insightPanels')"
        class="grid gap-4 xl:grid-cols-[1.15fr_1.35fr_1.1fr]"
      >
        <ReasonBreakdownPanel :items="pageData.reasonBreakdown" />
        <InventoryHealthPanel
          :before-metrics="pageData.inventoryHealthBefore"
          :after-metrics="pageData.inventoryHealthAfter"
          :summary="pageData.inventoryHealthSummary"
          :total-locations="pageData.totalLocations"
        />
        <BusinessImpactPanel :metrics="pageData.businessImpact" />
      </section>

      <section v-if="hasSection('tables')" class="grid gap-4 2xl:grid-cols-2">
        <TopInventoryWinsCard :rows="pageData.topInventoryWins" />
        <RedistributionActivityCard
          :rows="pageData.redistributionActivity"
          :pagination-label="pageData.activityPaginationLabel"
        />
      </section>

      <DashboardFooterBar
        v-if="hasSection('footerSummary')"
        :summary="pageData.footerSummary"
      />
    </template>
  </div>
</template>
